import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ConfirmDialog } from 'components';
import GalleryImagePicker from './GalleryImagePicker';
import GalleryItemDialog from './GalleryItemDialog';
import {
  countTabImages,
  createEmptySection,
  MAX_IMAGES_PER_TAB,
  SLIDER_ASSET_TAG_CODE,
  SLIDER_ASSET_TAG_LABEL,
  TAB_ASSET_TAG_CODE,
  TAB_ASSET_TAG_LABEL
} from './constants';


const GalleryTabSections = ({tabCode, values, onChange, errors}) => {
  const [itemDialogTarget, setItemDialogTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const tab = values.tabs[tabCode];
  const { sections } = tab;
  const sectionErrors = errors?.tabs?.[tabCode]?.sections;

  const setTabField = (field, value) => onChange(`tabs.${tabCode}.${field}`, value);
  const setSectionField = (index, field, value) => onChange(`tabs.${tabCode}.sections[${index}].${field}`, value);

  const handleAddSection = () => {
    setTabField('sections', [...sections, createEmptySection()]);
  };

  const handleItemSave = (item) => {
    const {sectionIndex, itemIndex} = itemDialogTarget;

    if (itemIndex === null) {
      setSectionField(sectionIndex, 'items', [...sections[sectionIndex].items, item]);
    } else {
      onChange(`tabs.${tabCode}.sections[${sectionIndex}].items[${itemIndex}]`, item);
    }

    setItemDialogTarget(null);
  };

  const handleConfirmDelete = () => {
    const {sectionIndex, itemIndex} = deleteTarget;

    if (itemIndex === undefined) {
      setTabField('sections', sections.filter((section, index) => index !== sectionIndex));
    } else {
      setSectionField(
        sectionIndex,
        'items',
        sections[sectionIndex].items.filter((item, index) => index !== itemIndex)
      );
    }

    setDeleteTarget(null);
  };

  return (
    <div>
      <Grid container spacing={3} className="mb-4">
        <Grid item xs={12} sm={2} className="my-2 ml-4">Enable Tab</Grid>
        <Grid item xs={12} sm={9}>
          <Switch
            color="primary"
            checked={tab.enabled == 1}
            onChange={(event) => setTabField('enabled', event.target.checked ? 1 : 0)}
          />
          <FormHelperText>
            Disabled tabs are not displayed on the gallery page
          </FormHelperText>
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Tab Label</Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            className="w-full max-w-450"
            variant="outlined"
            size="small"
            value={tab.label}
            onChange={(event) => setTabField('label', event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Tab Image</Grid>
        <Grid item xs={12} sm={9}>
          <GalleryImagePicker
            id={`before-after-gallery-${tabCode}-tab-image`}
            dialogTitle="Select Tab Image"
            value={tab.image}
            recommendedSize="150x150px"
            showClearButton
            assetTagCode={TAB_ASSET_TAG_CODE}
            assetTagLabel={TAB_ASSET_TAG_LABEL}
            onSelect={(imageUrl) => setTabField('image', imageUrl)}
            rowHeight={250}
          />
        </Grid>
      </Grid>
      <Divider className="my-8" />
      <div className="flex items-center justify-between mb-4">
        <Typography variant="h6">Sections ({sections.length})</Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleAddSection}
        >
          <Icon className="mr-2">add</Icon>
          Add Section
        </Button>
      </div>
      <FormHelperText className="mb-4">
        Each section is a procedure group (e.g. Facelift) with an optional before/after comparison slider
        and a list of gallery items. Up to {MAX_IMAGES_PER_TAB} images per tab across all sections and items
        ({countTabImages(sections)}/{MAX_IMAGES_PER_TAB} used).
      </FormHelperText>
      {typeof sectionErrors === 'string' &&
        <FormHelperText error className="mb-4">{sectionErrors}</FormHelperText>
      }
      {!sections.length &&
        <Typography variant="body2" className="my-4">
          No sections configured yet
        </Typography>
      }
      {sections.map((section, sectionIndex) => (
        <Accordion key={`${tabCode}-section-${sectionIndex}`}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="font-medium">
              {section.name || `Section ${sectionIndex + 1}`}
              {section.enabled != 1 && ' (disabled)'}
            </Typography>
            <Typography variant="body2" className="ml-4 my-auto">
              {section.items.length} item{section.items.length === 1 ? '' : 's'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className="flex-column">
            <Grid container spacing={3} className="mb-4">
              <Grid item xs={12} sm={2} className="my-2">Enable</Grid>
              <Grid item xs={12} sm={9}>
                <Switch
                  color="primary"
                  checked={section.enabled == 1}
                  onChange={(event) => setSectionField(sectionIndex, 'enabled', event.target.checked ? 1 : 0)}
                />
              </Grid>
              <Grid item xs={12} sm={2} className="my-2">Section Name</Grid>
              <Grid item xs={12} sm={9}>
                <TextField
                  className="w-full max-w-450"
                  variant="outlined"
                  size="small"
                  placeholder="e.g. Facelift"
                  value={section.name}
                  error={!!sectionErrors?.[sectionIndex]?.name}
                  helperText={sectionErrors?.[sectionIndex]?.name}
                  onChange={(event) => setSectionField(sectionIndex, 'name', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2} className="my-2">Identifier</Grid>
              <Grid item xs={12} sm={9}>
                <TextField
                  className="w-full max-w-450"
                  variant="outlined"
                  size="small"
                  placeholder="e.g. facelift"
                  value={section.identifier}
                  onChange={(event) => setSectionField(sectionIndex, 'identifier', event.target.value)}
                />
                <FormHelperText>
                  Used in the section anchor link. Leave empty to generate automatically
                </FormHelperText>
              </Grid>
              <Grid item xs={12} sm={2} className="my-2">Sort Order</Grid>
              <Grid item xs={12} sm={9}>
                <TextField
                  className="max-w-90"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={section.sort_order}
                  onChange={(event) => setSectionField(sectionIndex, 'sort_order', event.target.value)}
                />
              </Grid>
            </Grid>
            <Divider className="my-4" />
            <FormHelperText className="mb-2">
              Both before and after images must be set to show the section comparison slider. You can skip it to display section without a slider.
            </FormHelperText>
            <Grid container spacing={3} className="mb-4">
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" className="mb-2">Before Image</Typography>
                <GalleryImagePicker
                  id={`before-after-gallery-${tabCode}-section-${sectionIndex}-before`}
                  dialogTitle="Select Before Image"
                  value={section.before_image}
                  showClearButton
                  allowPng={false}
                  recommendedSize="500x500px"
                  assetTagCode={SLIDER_ASSET_TAG_CODE}
                  assetTagLabel={SLIDER_ASSET_TAG_LABEL}
                  rowHeight={250}
                  onSelect={(imageUrl) => setSectionField(sectionIndex, 'before_image', imageUrl)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" className="mb-2">After Image</Typography>
                <GalleryImagePicker
                  id={`before-after-gallery-${tabCode}-section-${sectionIndex}-after`}
                  dialogTitle="Select After Image"
                  value={section.after_image}
                  showClearButton
                  allowPng={false}
                  recommendedSize="500x500px"
                  assetTagCode={SLIDER_ASSET_TAG_CODE}
                  assetTagLabel={SLIDER_ASSET_TAG_LABEL}
                  rowHeight={250}
                  onSelect={(imageUrl) => setSectionField(sectionIndex, 'after_image', imageUrl)}
                />
              </Grid>
            </Grid>
            <Divider className="my-4" />
            <div className="flex items-center justify-between mb-2">
              <Typography variant="subtitle1">
                Items ({section.items.length})
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => setItemDialogTarget({sectionIndex, itemIndex: null})}
              >
                <Icon className="mr-2">add</Icon>
                Add Item
              </Button>
            </div>
            {typeof sectionErrors?.[sectionIndex]?.items === 'string' &&
              <FormHelperText error className="mb-2">{sectionErrors[sectionIndex].items}</FormHelperText>
            }
            {!section.items.length &&
              <Typography variant="body2" className="my-2">
                No items in this section yet
              </Typography>
            }
            {section.items.map((item, itemIndex) => (
              <div
                key={`${tabCode}-section-${sectionIndex}-item-${itemIndex}`}
                className="flex items-center justify-between py-2"
              >
                <div className="flex items-center">
                  {!!item.images.length &&
                    <img src={item.images[0]} alt={item.title} className="max-h-50 max-w-150 mr-4" />
                  }
                  <div>
                    <Typography variant="body1">
                      {item.title || `Item ${itemIndex + 1}`}
                      {item.enabled != 1 && ' (disabled)'}
                    </Typography>
                    <Typography variant="body2">
                      {item.images.length} image{item.images.length === 1 ? '' : 's'}
                    </Typography>
                  </div>
                </div>
                <div>
                  <IconButton
                    size="small"
                    title="Edit item"
                    onClick={() => setItemDialogTarget({sectionIndex, itemIndex})}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Delete item"
                    onClick={() => setDeleteTarget({sectionIndex, itemIndex})}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </div>
              </div>
            ))}
            <div className="flex justify-end mt-4">
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setDeleteTarget({sectionIndex})}
              >
                <Icon className="mr-2">delete</Icon>
                Delete Section
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
      <GalleryItemDialog
        open={!!itemDialogTarget}
        onClose={() => setItemDialogTarget(null)}
        onSave={handleItemSave}
        item={itemDialogTarget && itemDialogTarget.itemIndex !== null
          ? sections[itemDialogTarget.sectionIndex].items[itemDialogTarget.itemIndex]
          : null}
        surgeons={values.surgeons}
      />
      <ConfirmDialog
        title={deleteTarget?.itemIndex === undefined ? 'Delete Section' : 'Delete Item'}
        open={!!deleteTarget}
        setOpen={() => setDeleteTarget(null)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      >
        {deleteTarget?.itemIndex === undefined
          ? 'Are you sure you want to delete this section with all its items?'
          : 'Are you sure you want to delete this item?'}
      </ConfirmDialog>
    </div>
  );
};

export default GalleryTabSections;
